// src/components/CatalogBrowser.js
import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { productsPublicApi } from "../utils/apiProducts";

function logProductsApiError(context, error) {
  console.error(context, {
    status: error?.response?.status,
    message: error?.message,
  });
}

function fmtPrice(n, cur = "EUR") {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  try {
    return new Intl.NumberFormat("sr-RS", {
      style: "currency",
      currency: cur || "EUR",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${cur || ""}`.trim();
  }
}




export default function CatalogBrowser() {
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [hasImage, setHasImage] = useState(true);

  const [facets, setFacets] = useState({ brands: [], categories: [] });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(40);
  const [total, setTotal] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [minPrice, setMinPrice] = useState('');
const [maxPrice, setMaxPrice] = useState('');
const [source, setSource] = useState("all");

const [sort, setSort] = useState('title'); // 'title' | 'price'
const [dir, setDir]   = useState('asc');   // 'asc' | 'desc'

  useEffect(() => {
  setPage(1);
}, [brand, category, hasImage, minPrice, maxPrice, sort, dir]);

  async function loadFacets() {
    try {
      const { data } = await productsPublicApi.get("/api/catalog/facets");
      setFacets({
        brands: Array.isArray(data?.brands) ? data.brands : [],
        categories: Array.isArray(data?.categories) ? data.categories : [],
      });
    } catch (e) {
      logProductsApiError("Catalog facets error", e);
    }
  }

  async function loadList() {
    setLoading(true);
    try {
      const { data } = await productsPublicApi.get("/api/catalog/unified", {
  params: {
    page,
    pageSize,
    search: q || undefined,

    source: source || "all",
    brand: brand || undefined,
    category: category || undefined,

    hasImage: hasImage ? 1 : 0,
    minPrice: minPrice !== "" ? Number(minPrice) : undefined,
    maxPrice: maxPrice !== "" ? Number(maxPrice) : undefined,
    sort,
    dir,
  },
});


      setRows(data?.data || []);
      setTotal(data?.total || 0);
    } catch (e) {
      logProductsApiError("Catalog list error", e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  const debouncedReload = useMemo(
    () =>
      debounce(() => {
        setPage(1);
      }, 250),
    []
  );

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => {
  loadList();
}, [page, pageSize, brand, category, hasImage, minPrice, maxPrice, sort, dir]);
  useEffect(() => { debouncedReload(); return () => debouncedReload.cancel(); }, [q]);

  useEffect(() => {
    if (page === 1) loadList();
    // eslint-disable-next-line
  }, [q]);

  async function openDetails(row) {
    setSelected(row);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const { data } = await productsPublicApi.get(`/api/catalog/item/${encodeURIComponent(row.id)}`);
      setDetails(data);
    } catch (e) {
      logProductsApiError("Catalog details error", e);
    } finally {
      setDetailsLoading(false);
    }
  }

  function closeDetails() {
    setSelected(null);
    setDetails(null);
  }

  const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xl font-bold">Katalog</div>
        <div className="text-sm text-gray-500">{loading ? "Učitavam…" : `${total} proizvoda`}</div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Pretraga</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-72"
            placeholder="naziv / šifra / url…"
          />
        </div>

        <div className="flex flex-col">
  <label className="text-xs text-gray-500">Izvor</label>
  <select
    value={source}
    onChange={(e) => { setSource(e.target.value); setPage(1); }}
    className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-40"
  >
    <option value="all">Sve</option>
    <option value="tis">TIS</option>
    <option value="darex">Darex</option>
    <option value="pinoles">Pinoles</option>
  </select>
</div>


        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Brend</label>
          <select
            value={brand}
            onChange={(e) => { setBrand(e.target.value); setPage(1); }}
            className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-56"
          >
            <option value="">Svi</option>
            {facets.brands.map((b) => (
              <option key={b.brand} value={b.brand}>
                {b.brand} ({b.cnt})
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Kategorija</label>
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-72"
          >
            <option value="">Sve</option>
            {facets.categories.map((c) => (
              <option key={c.label} value={c.label}>
                {c.label} ({c.cnt})
              </option>
            ))}
          </select>
        </div>

        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
  <label>
    Min €
    <input
      type="number"
      value={minPrice}
      onChange={(e) => setMinPrice(e.target.value)}
      style={{ width: 120, marginLeft: 6 }}
      placeholder="0"
    />
  </label>

  <label>
    Max €
    <input
      type="number"
      value={maxPrice}
      onChange={(e) => setMaxPrice(e.target.value)}
      style={{ width: 120, marginLeft: 6 }}
      placeholder="9999"
    />
  </label>

  <button onClick={() => { setMinPrice(''); setMaxPrice(''); }}>
    Reset cena
  </button>

  <label style={{ marginLeft: 12 }}>
    Sort
    <select
      value={sort}
      onChange={(e) => setSort(e.target.value)}
      style={{ marginLeft: 6 }}
    >
      <option value="title">Naziv</option>
      <option value="price">Cena</option>
    </select>
  </label>

  <label>
    Smer
    <select
      value={dir}
      onChange={(e) => setDir(e.target.value)}
      style={{ marginLeft: 6 }}
    >
      <option value="asc">Rastuće</option>
      <option value="desc">Opadajuće</option>
    </select>
  </label>
</div>


        <label className="flex items-center gap-2 px-2 py-2">
          <input
            type="checkbox"
            checked={hasImage}
            onChange={(e) => { setHasImage(e.target.checked); setPage(1); }}
          />
          <span className="text-sm">Samo sa slikom</span>
        </label>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="px-2 py-2 border rounded dark:bg-neutral-800 dark:text-white"
          >
            {[20, 40, 80, 120, 200].map((n) => (
              <option key={n} value={n}>{n}/str</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid (lepše za katalog) */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {!rows.length && !loading && (
          <div className="col-span-full text-gray-500 p-3 border rounded">Nema rezultata.</div>
        )}

        {rows.map((r) => (
  <button
    key={r.id}
    className="text-left border rounded hover:shadow-sm dark:hover:bg-neutral-800 overflow-hidden"
    onClick={() => openDetails(r)}
  >
    <div className="aspect-square bg-gray-50 dark:bg-neutral-700">
      {r.main_image ? (
        <img
          src={r.main_image}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : null}
    </div>

    <div className="p-2 space-y-1">
      {/* ✅ NOVO: Source badge + (opciono) SKU */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] px-2 py-0.5 rounded border bg-gray-100 dark:bg-neutral-800 dark:border-neutral-700">
          {(r.source || "").toUpperCase()}
        </span>

        {r.sku ? <span className="text-[11px] text-gray-500">#{r.sku}</span> : null}
      </div>

      <div className="font-medium line-clamp-2">{r.title}</div>
      <div className="text-xs text-gray-500">
        {r.brand || ""}{r.unit ? ` • ${r.unit}` : ""}
      </div>
      <div className="font-semibold">
        {fmtPrice(r.price ?? null, r.currency || "EUR")}
      </div>
    </div>
  </button>
))}

      </div>

      {/* Pagination */}
      <div className="flex items-center gap-2">
        <button className="px-3 py-2 border rounded disabled:opacity-50" onClick={() => setPage(1)} disabled={page <= 1}>«</button>
        <button className="px-3 py-2 border rounded disabled:opacity-50" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
        <div className="text-sm text-gray-600 dark:text-gray-300">Strana {page} / {pageCount}</div>
        <button className="px-3 py-2 border rounded disabled:opacity-50" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount}>Next</button>
        <button className="px-3 py-2 border rounded disabled:opacity-50" onClick={() => setPage(pageCount)} disabled={page >= pageCount}>»</button>
      </div>

      {/* Details modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeDetails}>
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-lg max-w-5xl w-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-bold">{selected.title}</div>
                <div className="text-sm text-gray-500">{selected.sku} • {selected.brand}</div>
              </div>
              <button className="px-3 py-2 border rounded" onClick={closeDetails}>Close</button>
            </div>

            {detailsLoading && <div className="py-4 text-gray-500">Učitavam detalje…</div>}

            {details && !detailsLoading && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold mb-2">Slike</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(details.images || []).map((im, idx) => (
                      <a key={idx} href={im.src || im.url} target="_blank" rel="noreferrer">
                        <img src={im.src || im.url} alt="" className="w-full h-24 object-cover rounded border" loading="lazy" />
                      </a>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="font-semibold mb-2">Cena</div>
                  <div className="text-2xl font-bold">{fmtPrice(details.price, details.currency || "EUR")}</div>

                  <div className="font-semibold mt-4 mb-2">Specifikacija</div>
                  <div className="max-h-72 overflow-auto border rounded">
                    <table className="min-w-full text-sm">
                      <tbody>
                        {(details.attributes || []).map((a, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 font-medium">{a.name}</td>
                            <td className="p-2">{a.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(details.variants || []).length > 0 && (
                    <>
                      <div className="font-semibold mt-4 mb-2">Varijante</div>
                      <div className="max-h-64 overflow-auto border rounded">
                        <table className="min-w-full text-sm">
                          <thead className="sticky top-0 bg-white dark:bg-neutral-900">
                            <tr className="border-b">
                              <th className="p-2 text-left">Naziv</th>
                              <th className="p-2 text-left">SKU</th>
                              <th className="p-2 text-left">Cena</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.variants.map((v, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="p-2">
                                  {v.url ? (
                                    <a className="text-blue-600 underline" href={v.url} target="_blank" rel="noreferrer">{v.name}</a>
                                  ) : v.name}
                                  {v.section_title ? <div className="text-[11px] text-gray-500">{v.section_title}</div> : null}
                                </td>
                                <td className="p-2">{v.sku || ""}</td>
                                <td className="p-2">{fmtPrice(v.price, v.currency || details.currency || "EUR")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* <div className="mt-3">
                    <a className="text-blue-600 underline" href={details.url} target="_blank" rel="noreferrer">
                      Original link
                    </a>
                  </div> */}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
